import React, { FC, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import ChatSidebar from './sidebar/ChatSidebar';
import ChatHeader from './ChatHeader';
import ChatCanvas from './canvas/ChatCanvas';
import {
  ChatCanvasProvider,
  useChatCanvas,
} from '../../contexts/ChatCanvasContext';
import { SidebarDrawerProvider } from '../../contexts/SidebarDrawerContext';
import {
  ChatSidebarCollapseProvider,
  useChatSidebarCollapse,
} from '../../contexts/ChatSidebarCollapseContext';
import { ConfirmationsProvider } from '../../contexts/ConfirmationsContext';
import { usePusher } from '../../contexts/PusherContext';
import SidebarDrawer from './sidebar/SidebarDrawer';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import api from '../../api';
import {
  ThreadLayoutType,
  GetThreadLayoutTypeResponse,
  AgentEvents,
} from '@dreamai/types';

interface ChatInterfaceContentProps {
  threadId: string | null;
}

const ChatInterfaceContent: FC<ChatInterfaceContentProps> = ({ threadId }) => {
  const { chatCanvasRef } = useChatCanvas();
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const { isCollapsed } = useChatSidebarCollapse();

  // Thread layout type state
  const [layoutType, setLayoutType] = useState<ThreadLayoutType>('tab');
  const [discussingObject, setDiscussingObject] = useState<
    string | undefined
  >();
  const [discussingObjectMatch, setDiscussingObjectMatch] = useState<
    Record<string, any> | undefined
  >();

  // Mobile sidebar state
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  // Track if there are canvas assets
  const [hasCanvasAssets, setHasCanvasAssets] = useState(false);

  // Fetch layout type for the current thread
  const fetchLayoutType = useCallback(async () => {
    if (!threadId) {
      setLayoutType('tab');
      setDiscussingObject(undefined);
      setDiscussingObjectMatch(undefined);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetThreadLayoutTypeResponse>(
        `/agents/threads/${threadId}/layout-type`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setLayoutType(response.data.layoutType);
        setDiscussingObject(response.data.discussingObject);
        setDiscussingObjectMatch(response.data.discussingObjectMatch);
      }
    } catch (error) {
      console.error('Error fetching thread layout type:', error);
      setLayoutType('tab');
      setDiscussingObject(undefined);
      setDiscussingObjectMatch(undefined);
    }
  }, [threadId, getAccessTokenSilently]);

  // Fetch layout type when threadId changes
  useEffect(() => {
    fetchLayoutType();
  }, [fetchLayoutType]);

  // Subscribe to Pusher event for discussing objects changes
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherInstance) return;

    const channelName = `${process.env.REACT_APP_PUSHER_ENV}-private-thread-${threadId}`;
    const channel = pusherContext.pusherInstance.subscribe(channelName);

    const handleDiscussingObjectsChanged = () => {
      console.log('Discussing objects changed, refetching layout type');
      fetchLayoutType();
    };

    channel.bind(
      AgentEvents.THREAD_DISCUSSING_OBJECTS_CHANGED,
      handleDiscussingObjectsChanged,
    );

    return () => {
      channel.unbind(
        AgentEvents.THREAD_DISCUSSING_OBJECTS_CHANGED,
        handleDiscussingObjectsChanged,
      );
      // Don't unsubscribe here as other components may be using the channel
    };
  }, [threadId, pusherContext?.pusherInstance, fetchLayoutType]);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && sidebarExpanded) {
        const sidebar = document.querySelector('.chat-sidebar-container');
        const toggleButton = document.querySelector('.sidebar-toggle-button');

        if (
          sidebar &&
          toggleButton &&
          !sidebar.contains(event.target as Node) &&
          !toggleButton.contains(event.target as Node)
        ) {
          setSidebarExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, sidebarExpanded]);

  return (
    <div className='chat-interface inset-white-backed'>
      {/* Auto-collapse sidebar for live campaign threads - TEMPORARILY DISABLED */}
      {/* <LiveCampaignAutoCollapse
        discussingObject={discussingObject}
        discussingObjectMatch={discussingObjectMatch}
      /> */}

      {/* Header shown here only when no canvas assets */}
      {!hasCanvasAssets && <ChatHeader threadId={threadId} />}

      <div className='chat-layout'>
        <div
          className={`chat-sidebar-container ${
            isMobile
              ? sidebarExpanded
                ? 'mobile-expanded'
                : 'mobile-collapsed'
              : ''
          }${isCollapsed ? ' chat-collapsed' : ''}${!hasCanvasAssets ? ' no-canvas-assets' : ''}`}
        >
          <ChatSidebar
            layoutType={layoutType}
            hasCanvasAssets={hasCanvasAssets}
            threadId={threadId}
          />
          <SidebarDrawer
            onThreadSelect={(threadId, agentId, readMarker) => {
              // Navigate to the thread
              const params = new URLSearchParams();
              if (agentId) params.set('agent_id', agentId);
              params.set('thread_id', threadId);
              window.location.search = params.toString();
            }}
          />
        </div>

        {/* Mobile sidebar toggle button */}
        {isMobile && (
          <button
            className={`sidebar-toggle-button ${sidebarExpanded ? 'expanded' : 'collapsed'}`}
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <ArrowRightIcon />
          </button>
        )}

        {/* Collapse button for app layout threads - TEMPORARILY DISABLED */}
        {/* {!isMobile && <SidebarCollapseButton layoutType={layoutType} />} */}

        {threadId && (
          <div
            className={`chat-canvas-container${!hasCanvasAssets ? ' no-canvas-assets' : ''}`}
          >
            <ChatCanvas
              ref={chatCanvasRef}
              layoutType={layoutType}
              discussingObject={discussingObject}
              discussingObjectMatch={discussingObjectMatch}
              onHasAssets={setHasCanvasAssets}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ChatInterface: FC = () => {
  const [searchParams] = useSearchParams();
  const threadId = searchParams.get('thread_id');

  return (
    <SidebarDrawerProvider>
      <ChatCanvasProvider>
        <ChatSidebarCollapseProvider threadId={threadId}>
          <ConfirmationsProvider threadId={threadId}>
            <ChatInterfaceContent threadId={threadId} />
          </ConfirmationsProvider>
        </ChatSidebarCollapseProvider>
      </ChatCanvasProvider>
    </SidebarDrawerProvider>
  );
};

export default ChatInterface;
